import React from "react";
import { Row, Col, Form } from "antd";
import { Booking } from "../../../types/booking";
import { PaymentTerm } from "../../../types/invoice";
import { getPaymentTermsForDisplay } from "../../../utils/paymentTermsUtils";

interface PricingPaymentTerm {
  id: number;
  days: number;
  interest_vat_import_duties: number;
  created_at: string;
}

interface PaymentTermsSectionProps {
  selectedBooking: Booking | null;
  paymentTerm: PaymentTerm;
  customPaymentTerms: string;
}

const PaymentTermsSection: React.FC<PaymentTermsSectionProps> = ({
  selectedBooking,
  paymentTerm,
  customPaymentTerms,
}) => {
  if (!selectedBooking) {
    return null;
  }

  // Create a mock invoice object for payment terms calculation
  const mockInvoice = {
    payment_term: paymentTerm,
    custom_payment_terms: customPaymentTerms,
    booking: selectedBooking
      ? {
          company_info: selectedBooking.company_info,
        }
      : undefined,
  } as const;

  // Type assertion is needed due to interface mismatch between Company and CompanyInfo
  const paymentTerms = getPaymentTermsForDisplay(
    mockInvoice as Parameters<typeof getPaymentTermsForDisplay>[0]
  );

  const sourceIcons = {
    custom: "🎯",
    invoice: "📋",
    company: "🏢",
  };

  const sourceColors = {
    custom: "#722ed1",
    invoice: "#1890ff",
    company: "#52c41a",
  };

  return (
    <Row gutter={8}>
      <Col span={24}>
        <Form.Item
          label="Payment Terms (for Moneybird)"
          style={{ marginBottom: 8 }}
        >
          <div
            style={{
              border: "1px solid #d9d9d9",
              borderRadius: "6px",
              padding: "8px 12px",
              backgroundColor: "#fafafa",
            }}
          >
            <div
              style={{
                fontSize: "14px",
                fontWeight: "bold",
                color:
                  sourceColors[
                    paymentTerms.source as keyof typeof sourceColors
                  ],
                marginBottom: "4px",
              }}
            >
              {sourceIcons[paymentTerms.source as keyof typeof sourceIcons]}{" "}
              {paymentTerms.fullDisplay}
            </div>
            <div style={{ fontSize: "12px", color: "#666" }}>
              {paymentTerms.source === "custom"
                ? "🎯 Using custom payment terms"
                : paymentTerms.source === "invoice"
                ? "📋 Using invoice-specific payment terms"
                : "🏢 Using company default payment terms"}
            </div>
            <div
              style={{
                fontSize: "12px",
                color: "#fa8c16",
                marginTop: "2px",
              }}
            >
              📅 Due Date: {paymentTerms.dueDate}
            </div>
            {paymentTerms.source === "company" &&
              selectedBooking?.company_info &&
              (() => {
                const company = Array.isArray(selectedBooking.company_info)
                  ? selectedBooking.company_info[0]
                  : selectedBooking.company_info;
                const paymentTerm = company?.pricing_payment_term;

                // Type guard to check if paymentTerm is a PricingPaymentTerm object
                const isPricingPaymentTerm = (
                  term: unknown
                ): term is PricingPaymentTerm => {
                  return Boolean(
                    term &&
                      typeof term === "object" &&
                      term !== null &&
                      "interest_vat_import_duties" in term
                  );
                };

                let interestRate = 0;

                if (Array.isArray(paymentTerm) && paymentTerm.length > 0) {
                  const firstTerm = paymentTerm[0];
                  if (isPricingPaymentTerm(firstTerm)) {
                    interestRate = firstTerm.interest_vat_import_duties;
                  }
                } else if (isPricingPaymentTerm(paymentTerm)) {
                  interestRate = paymentTerm.interest_vat_import_duties;
                }

                return interestRate > 0 ? (
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#fa8c16",
                      marginTop: "2px",
                    }}
                  >
                    💰 Interest Rate: {interestRate}% (on import VAT and duties)
                  </div>
                ) : null;
              })()}
          </div>
        </Form.Item>
      </Col>
    </Row>
  );
};

export default PaymentTermsSection;
